import type { UpdateTaskRequest } from "@9amhealth/openapi";
import { TaskResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useEffect } from "react";
import type { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import type {
  TaskKey,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";

const DebugTaskMocker: FC = () => {
  const [
    { tasks },
    {
      updateTaskStatus,
      setMockResponseForProgram,
      mockProgramResponsesEnabled,
      setMockProgramResponsesEnabled,
      mockProgramName,
      mockProgramResponses
    }
  ] = useBlocNext(TaskManagementBloc);
  const availableStatus = Object.values(TaskResponse.status);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [mockProgram, setMockProgram] = React.useState<string>(mockProgramName);
  const [mockProgramEnabled, setMockProgramEnabled] = React.useState<boolean>(
    mockProgramResponsesEnabled
  );
  const [mockProgramResponse, setMockProgramResponse] =
    React.useState<string>("");
  const [responseMockValid, setResponseMockValid] =
    React.useState<boolean>(false);

  const handleMockProgramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMockProgram(e.target.value);
  };

  const handleMockProgramResponseChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setMockProgramResponse(e.target.value);
    try {
      setMockResponseForProgram(mockProgram, e.target.value);
      setResponseMockValid(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error parsing mock response:", error);
      setResponseMockValid(false);
    }
  };

  useEffect(() => {
    if (mockProgram && mockProgramResponses[mockProgram]) {
      try {
        setMockProgramResponse(
          JSON.stringify(mockProgramResponses[mockProgram], null, 2)
        );
        setResponseMockValid(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error parsing mock response:", error);
        setResponseMockValid(false);
      }
    }
  }, [mockProgram, mockProgramResponses]);

  const handleMockProgramEnabledChange = (enabled: boolean) => {
    setMockProgramEnabled(enabled);
    setMockProgramResponsesEnabled(enabled);
  };

  const programs = new Set<KnownProgram>();
  const groups = new Set<string>();
  const slugs = new Set<string>();

  tasks.forEach((task) => {
    programs.add(task.program as KnownProgram);
    groups.add(task.group);
    slugs.add(task.slug);
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const values = new FormData(e.currentTarget);

    const t = {
      program: values.get("program") as KnownProgram,
      group: values.get("group") as string,
      slug: values.get("slug") as TaskKey
    };

    const status = values.get("status") as UpdateTaskRequest.status;
    setError("");
    setLoading(true);
    void updateTaskStatus(t as TaskResponseKnown, status)
      .catch((tmError: unknown) => {
        setError(String((tmError as { message?: string }).message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <label>
          Program:
          <input type="text" name="program" list="programs" />
          <datalist id="programs">
            {Array.from(programs).map((program) => {
              return (
                <option key={program} value={program}>
                  {program}
                </option>
              );
            })}
          </datalist>
        </label>
        <br />

        <label>
          Group:
          <input type="text" name="group" list="groups" />
          <datalist id="groups">
            {Array.from(groups).map((group) => {
              return (
                <option key={group} value={group}>
                  {group}
                </option>
              );
            })}
          </datalist>
        </label>
        <br />

        <label>
          Slug:
          <input type="text" name="slug" list="slugs" />
          <datalist id="slugs">
            {Array.from(slugs).map((slug) => {
              return (
                <option key={slug} value={slug}>
                  {slug}
                </option>
              );
            })}
          </datalist>
        </label>
        <br />

        <label>
          Status:
          <select name="status">
            {availableStatus.map((status) => {
              return (
                <option key={status} value={status}>
                  {status}
                </option>
              );
            })}
          </select>
        </label>
        <br />
        <button type="submit">Submit</button>

        {loading && <p>Loading...</p>}
        {error && <p>{error}</p>}
      </form>
      <hr />

      <h2>Mock Task Response</h2>

      <label>
        Enabled:
        <br />
        <input
          type="checkbox"
          name="mockTaskResponse"
          checked={!!mockProgramEnabled}
          onChange={(e) => {
            handleMockProgramEnabledChange(e.target.checked);
          }}
        />
      </label>
      <br />
      <br />

      <label>
        Program:
        <br />
        <input
          name="program"
          type="text"
          value={mockProgram}
          onInput={handleMockProgramChange}
          list="programs"
        />
        <datalist id="programs">
          {Array.from(programs).map((program) => {
            return (
              <option key={program} value={program}>
                {program}
              </option>
            );
          })}
        </datalist>
      </label>
      <br />
      <br />
      <label>
        Response:
        <br />
        <textarea
          name="mockProgramResponse"
          rows={10}
          cols={50}
          value={mockProgramResponse}
          onInput={handleMockProgramResponseChange}
        />
      </label>
      <br />
      <br />
      {!responseMockValid && (
        <p style={{ color: "red" }}>Mock response is invalid</p>
      )}
    </>
  );
};

export default DebugTaskMocker;
